/**
 * Shared types for the Guest Checkout analytics layer.
 *
 * The analytics layer is the single place that knows about Google Analytics 4,
 * Google Tag Manager and the `window.postMessage` tracking contract. Components
 * only ever depend on the typed facade exported from `./index`.
 */
import type { ConsentCategories } from "../services/cookieConsent/types";
import type { HostConsentRelay } from "./hostConsent";

/** Commercial owner of the journey, mirroring the legacy dmn booking widget. */
export type TrackingSource = "dmn" | "partner";

/**
 * Funnel selection events, raised as the guest fills in the booking form.
 *
 * GA4 has no recommended name for choosing a venue, a date or a party size, so
 * each is a custom event named after the selection it reports, in GA4's
 * `verb_noun` style. They are deliberately not modelled as the legacy Universal
 * Analytics category/action/label triple.
 */
export type AnalyticsFunnelEventName =
  | "select_venue"
  | "select_booking_type"
  | "select_booking_date"
  | "select_party_size"
  | "select_booking_time";

/** Analytics events emitted by the checkout, using GA4 recommended names. */
export type AnalyticsEventName =
  | "widget_loaded"
  | AnalyticsFunnelEventName
  | "begin_checkout"
  | "add_to_cart"
  | "remove_from_cart"
  | "payment_initiated"
  | "purchase"
  | "booking";

/**
 * Legacy DesignMyNight names for events that are fired under GA4's recommended
 * name, keyed by the name actually fired.
 *
 * Only the pre-order events differ: story 2482590 specifies them as
 * `pre_order_added` / `pre_order_removed`, while GA4 ecommerce reporting
 * requires `add_to_cart` / `remove_from_cart`. Firing both names would
 * double-count in GA4, so one event is fired under its GA4 name and carries the
 * legacy name as {@link AnalyticsMessage.dmn_event_alias}.
 */
export const ANALYTICS_EVENT_ALIASES = {
  add_to_cart: "pre_order_added",
  remove_from_cart: "pre_order_removed",
} as const satisfies Partial<Record<AnalyticsEventName, string>>;

/** Legacy name an event may also be known by. */
export type AnalyticsEventAlias = (typeof ANALYTICS_EVENT_ALIASES)[keyof typeof ANALYTICS_EVENT_ALIASES];

/** A single GA4 ecommerce item. */
export interface AnalyticsItem {
  readonly item_id: string;
  readonly item_name: string;
  readonly item_category?: string;
  readonly item_brand?: string;
  readonly price?: number;
  readonly quantity?: number;
}

/** GA4 ecommerce payload shared by every checkout event. */
export interface AnalyticsEcommercePayload {
  readonly currency: string;
  readonly value: number;
  /**
   * Which checkout journey produced the event — always `JOURNEY_VERSION` from
   * `journey.ts`. Carried by every ecommerce payload so this journey can be told
   * apart from the legacy booking widget, which carries no marker at all.
   */
  readonly journey_version: string;
  readonly transaction_id?: string;
  /**
   * Correlates one payment journey across `payment_initiated` and `purchase`.
   * The pending booking id on a new booking, the booking id on an existing one,
   * so the two events match even where the final booking id differs from the
   * pending one. Count distinct values to measure completion.
   */
  readonly payment_journey_id?: string;
  /** The venue being booked, on the widget and funnel events. */
  readonly venue_id?: string;
  /** The chosen date (`YYYY-MM-DD`), once the guest has picked one. */
  readonly booking_date?: string;
  /** The chosen time (`HH:mm`), once the guest has picked one. */
  readonly booking_time?: string;
  readonly items: readonly AnalyticsItem[];
}

/**
 * The object pushed to `window.dataLayer`, in Google's documented format.
 *
 * Every dispatched envelope embeds these keys verbatim, so an integrator can
 * hand the envelope straight to their own `dataLayer` and read it with Google's
 * own GA4 ecommerce documentation.
 */
export type AnalyticsDataLayerEvent = {
  readonly event: AnalyticsEventName;
  readonly ecommerce: AnalyticsEcommercePayload;
};

/**
 * Envelope dispatched to the host page for every tracked event, as a DOM
 * `CustomEvent` in a same-document embed and by `postMessage` across an iframe
 * boundary.
 *
 * It is deliberately the union of two shapes, whose keys do not collide:
 * Google's `dataLayer` format ({@link AnalyticsDataLayerEvent}), so the whole
 * envelope can be pushed to a container as it stands, and our own metadata,
 * which is what lets a listener recognise and filter our messages. `payload`
 * duplicates `ecommerce` and is retained for the integrators already reading it.
 */
export interface AnalyticsMessage extends AnalyticsDataLayerEvent {
  readonly source: "collins-guest-checkout";
  readonly version: 1;
  readonly trackingSource: TrackingSource;
  /**
   * The journey cohort, repeated at the envelope's top level so a host-page
   * listener can filter on it without reading into `ecommerce`. Always the same
   * value as {@link AnalyticsEcommercePayload.journey_version}, and distinct
   * from {@link version}, which is the envelope's own protocol version.
   */
  readonly journey_version: string;
  /** The same object as {@link AnalyticsDataLayerEvent.ecommerce}, under the original key. */
  readonly payload: AnalyticsEcommercePayload;
  /**
   * The legacy name this event is also known by, where one exists — see
   * {@link ANALYTICS_EVENT_ALIASES}. Absent entirely for events that have only
   * ever had one name, and never pushed to `window.dataLayer`, so it cannot
   * appear as event data in GA4.
   */
  readonly dmn_event_alias?: AnalyticsEventAlias;
}

/** Venue context used to brand ecommerce items. */
export interface AnalyticsVenue {
  readonly id?: string;
  readonly title?: string;
  readonly currency?: string;
}

/** Runtime configuration supplied once, when the widget boots. */
export interface AnalyticsConfig {
  /** `?source=` — anything other than a partner value is treated as DMN owned. */
  readonly source?: string | null;
  /** `?affiliate_id=` — its presence always makes the journey partner owned. */
  readonly affiliateId?: string | null;
  /** `?ga_account=` — the partner's own GA4 measurement ID. */
  readonly partnerGaAccount?: string | null;
  /** `?gtm_account=` — the partner's own GTM container ID. */
  readonly partnerGtmAccount?: string | null;
  /**
   * `?gtm_consent=` — the consent decision an embedding site relayed, or `null`
   * when it relayed none. Used only where the checkout does not own the
   * decision: see `docs/consent.md`.
   */
  readonly hostConsent?: ConsentCategories | null;
  /**
   * The relayed decision and what the safeguards made of it, where a relay was
   * present. Carried so the outcome can be audited server side; the decision
   * itself is {@link hostConsent}.
   */
  readonly hostConsentRelay?: HostConsentRelay;
  /** Venue context for ecommerce payloads. */
  readonly venue?: AnalyticsVenue;
}

/**
 * The identifying half of {@link AnalyticsConfig}: who owns the journey and
 * where their own measurement goes.
 *
 * It travels two ways — on the checkout URL, which is our own hand-off, and as
 * attributes on the custom element, which is the embedder's statement in an
 * inline embed where the URL is the venue's own. See `docs/analytics.md`.
 */
export type PartnerAnalyticsContext = {
  readonly [Key in "source" | "affiliateId" | "partnerGaAccount" | "partnerGtmAccount"]: string | null;
};

/** Booking context needed to build conversion payloads. */
export interface AnalyticsBooking {
  readonly id?: string;
  readonly typeId?: string;
  readonly typeName?: string;
  readonly numPeople?: number;
  readonly value?: number;
  readonly currency?: string;
  readonly status?: string;
  /** Chosen date, `YYYY-MM-DD`. */
  readonly date?: string;
  /** Chosen time, `HH:mm`. */
  readonly time?: string;
  /**
   * Stable, non-PII identifier of the payment journey, reported as
   * `payment_journey_id`. Unlike `id` it does not change when a pending
   * booking becomes a booking.
   */
  readonly journeyId?: string;
}
